// bluaIQ — M2: Suppliers Directory
// View, search, add DFW suppliers

import { apiFetch } from '../utils/api'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import { Plus, Search, MapPin, Phone, Mail, Star, Building2 } from 'lucide-react'

// ── TYPES ─────────────────────────────────────────

interface Supplier {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
  phone?: string
  email?: string
  serviceRadius?: number
  rating?: number
  notes?: string
}

// ── COMPONENT ─────────────────────────────────────

export default function SuppliersModule() {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)

  const { data: suppliers, isLoading } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn: async () => { const token = await getToken(); return apiFetch('/api/suppliers', { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
  })

  const filtered = suppliers?.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.city?.toLowerCase().includes(search.toLowerCase())
  ) || []

  return (
    <div className="p-5 max-w-4xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{t('Suppliers')}</h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {suppliers?.length || 0} {t('suppliers in directory')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 bg-[#06539B] text-white text-[13px] font-[800] px-4 py-2.5 rounded-full"
        >
          <Plus size={14} />
          {t('Add Supplier')}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9890]" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t('Search suppliers...')}
          className="w-full border border-[#ECEAE4] rounded-full pl-9 pr-4 py-2.5 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]"
        />
      </div>

      {/* New supplier form */}
      {showForm && (
        <NewSupplierForm
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); queryClient.invalidateQueries({ queryKey: ['suppliers'] }) }}
        />
      )}

      {/* Supplier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map(supplier => (
          <SupplierCard key={supplier.id} supplier={supplier} />
        ))}
      </div>

      {filtered.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <Building2 size={36} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No suppliers found')}</p>
        </div>
      )}
    </div>
  )
}

// ── SUPPLIER CARD ─────────────────────────────────

function SupplierCard({ supplier }: { supplier: Supplier }) {
  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 hover:border-[#06539B] transition-colors">
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-[13px] font-[800] text-[#1C1A17]">{supplier.name}</h3>
        {supplier.rating && (
          <div className="flex items-center gap-0.5">
            <Star size={10} className="text-[#06539B] fill-[#06539B]" />
            <span className="text-[10px] font-[800] text-[#06539B]">{supplier.rating.toFixed(1)}</span>
          </div>
        )}
      </div>

      {(supplier.address || supplier.city) && (
        <div className="flex items-center gap-1 mb-1.5">
          <MapPin size={10} className="text-[#9E9890] flex-shrink-0" />
          <span className="text-[10px] font-[600] text-[#9E9890]">
            {supplier.address}{supplier.city ? `, ${supplier.city}` : ''} {supplier.state || ''}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 mt-2">
        {supplier.phone && (
          <a href={`tel:${supplier.phone}`} className="flex items-center gap-1 text-[10px] font-[700] text-[#3B82F6]">
            <Phone size={10} /> {supplier.phone}
          </a>
        )}
        {supplier.email && (
          <a href={`mailto:${supplier.email}`} className="flex items-center gap-1 text-[10px] font-[700] text-[#3B82F6]">
            <Mail size={10} /> {supplier.email}
          </a>
        )}
      </div>

      {supplier.serviceRadius && (
        <span className="inline-block mt-2 text-[9px] font-[700] px-2 py-0.5 rounded-full bg-[#F2F0EB] text-[#9E9890]">
          {supplier.serviceRadius} mi radius
        </span>
      )}
    </div>
  )
}

// ── NEW SUPPLIER FORM ─────────────────────────────

function NewSupplierForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { getToken } = useAuth()
  const { t } = useTranslation()
  const [form, setForm] = useState({ name: '', address: '', city: '', state: 'TX', phone: '', email: '' })

  const mutation = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch('/api/suppliers', {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    }).then(r => r.json()) },
    onSuccess: onCreated
  })

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
      <h3 className="text-[13px] font-[800]">{t('Add Supplier')}</h3>
      <input placeholder={t('Supplier name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
        className="w-full border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      <div className="grid grid-cols-3 gap-2">
        <input placeholder={t('Address')} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder={t('City')} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder="TX" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} maxLength={2}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input placeholder={t('Phone')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
        <input placeholder={t('Email')} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
          className="border border-[#ECEAE4] rounded-lg px-3 py-2 text-[14px] font-[600] focus:outline-none focus:border-[#06539B]" />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onClose} className="flex-1 border border-[#ECEAE4] rounded-full py-2 text-[11px] font-[800] text-[#9E9890]">{t('Cancel')}</button>
        <button onClick={() => mutation.mutate()} disabled={!form.name || mutation.isPending}
          className="flex-1 bg-[#06539B] text-white rounded-full py-2 text-[11px] font-[800] disabled:opacity-40">
          {mutation.isPending ? '...' : t('Save')}
        </button>
      </div>
    </div>
  )
}
